import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { ImageIcon, Loader2, Expand, Camera, Pencil, Upload } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { EntityWidgetProps } from "@/other/constants/EntityManagerConfig";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { EntityFileProvider, useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import { PresignedFilePreview } from "@/react/components/entityViews/files/components/PresignedFilePreview";
import { EntityFileDropOverlay } from "@/react/components/entityViews/files/components/EntityFileDropOverlay";
import { useEntityFileUpload } from "@/react/components/entityViews/files/hooks/useEntityFileUpload";
import { useFileDragAndDrop } from "@/react/components/entityViews/files/hooks/useFileDragAndDrop";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { cn } from "@/modern/utils";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { FileActionsMenu } from "@/react/components/entityViews/files/components/FileActionsMenu";
import { useFileSpotIncentive } from "@/react/components/entityViews/files/hooks/useFileSpotIncentive";
import type { FileViewerMode } from "@/react/components/entityViews/files/components/editors/FileDetailModal/types";

const transition = { duration: 0.15, ease: "easeInOut" as const };

const GRID_COLS = 3;
const GRID_GAP = 6;
const GRID_PAD = 12;
/** Above this cell size, load the full file URL instead of a soft thumbnail. */
const THUMBNAIL_MAX_CELL_PX = 800;

const MediaGridCell = ({
  file,
  onOpen,
  onAnnotate,
  onAnalyze,
  onPromote,
  onAdjustPoints,
  onSummaryShare,
  onDownload,
  onDelete,
  shouldShowAdjustPoints,
  preferThumbnail,
}: {
  file: SwivuFile;
  onOpen: (file: SwivuFile) => void;
  onAnnotate: (file: SwivuFile) => void;
  onAnalyze: (file: SwivuFile) => void;
  onPromote: (file: SwivuFile) => void;
  onAdjustPoints: (file: SwivuFile) => void;
  onSummaryShare: (file: SwivuFile) => void;
  onDownload: (file: SwivuFile) => void;
  onDelete: (fileId: number) => void;
  shouldShowAdjustPoints: (file: SwivuFile) => boolean;
  preferThumbnail: boolean;
}) => {
  return (
    <button
      type="button"
      aria-label={file.name || "Open media"}
      onClick={() => onOpen(file)}
      className={cn(
        "group relative aspect-square overflow-hidden rounded-md bg-muted cursor-pointer border transition-all duration-200",
        file.isAnnotated
          ? "border-primary/40 hover:border-primary hover:shadow-primary/30"
          : "border-transparent hover:border-primary/50",
      )}
    >
      <PresignedFilePreview
        file={file}
        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
        compactLoading
        isThumbnail={preferThumbnail}
        preferThumbnail={preferThumbnail}
      />

      {file.isAnnotated && (
        <div className="absolute bottom-0 left-0 overflow-hidden w-10 h-10 pointer-events-none z-10">
          <div className="absolute -left-6 bottom-0 w-16 text-center transform rotate-45 bg-primary text-primary-foreground py-0.5 shadow-lg flex items-center justify-center">
            <Pencil className="w-2 h-2 -rotate-45" />
          </div>
        </div>
      )}

      {file.source === "companycam" && (
        <div className="absolute top-1 left-1 z-10 pointer-events-none">
          <div className="flex items-center justify-center size-5 rounded-full bg-black/50 backdrop-blur-sm">
            <Camera className="size-3 text-white" />
          </div>
        </div>
      )}

      <div className="absolute top-1 right-1 z-20">
        <FileActionsMenu
          file={file}
          onFileDownload={onDownload}
          onFileDelete={onDelete}
          onView={onOpen}
          onAnnotate={onAnnotate}
          onAnalyze={onAnalyze}
          onPromote={onPromote}
          onAdjustPoints={onAdjustPoints}
          onSummaryShare={onSummaryShare}
          shouldShowAdjustPoints={shouldShowAdjustPoints}
          triggerClassName="bg-background/90 shadow-sm"
        />
      </div>

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-6 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <p className="truncate text-xs text-white">{file.name}</p>
      </div>
    </button>
  );
};

const MediaGrid = ({
  images,
  onOpen,
  onAnnotate,
  onAnalyze,
  onPromote,
  onAdjustPoints,
  onSummaryShare,
  onDownload,
  onDelete,
  shouldShowAdjustPoints,
  scrollRef,
  hasMore,
  isLoading,
  onLoadMore,
}: {
  images: SwivuFile[];
  onOpen: (file: SwivuFile) => void;
  onAnnotate: (file: SwivuFile) => void;
  onAnalyze: (file: SwivuFile) => void;
  onPromote: (file: SwivuFile) => void;
  onAdjustPoints: (file: SwivuFile) => void;
  onSummaryShare: (file: SwivuFile) => void;
  onDownload: (file: SwivuFile) => void;
  onDelete: (fileId: number) => void;
  shouldShowAdjustPoints: (file: SwivuFile) => boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(100);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.clientWidth;
      const available = w - GRID_PAD * 2 - GRID_GAP * (GRID_COLS - 1);
      setCellSize(Math.floor(available / GRID_COLS));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const preferThumbnail = cellSize <= THUMBNAIL_MAX_CELL_PX;
  const rowCount = Math.ceil(images.length / GRID_COLS);
  const rowHeight = cellSize + GRID_GAP;

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 2,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [cellSize, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();

  return (
    <div ref={containerRef} className="w-full" style={{ padding: GRID_PAD }}>
      <div className="relative w-full" style={{ height: totalHeight }}>
        {virtualRows.map((virtualRow) => {
          const startIdx = virtualRow.index * GRID_COLS;
          const rowFiles = images.slice(startIdx, startIdx + GRID_COLS);

          return (
            <div
              key={virtualRow.index}
              className="absolute left-0 top-0 w-full"
              style={{
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="grid grid-cols-3 h-full" style={{ gap: GRID_GAP }}>
                {rowFiles.map((file) => (
                  <MediaGridCell
                    key={file.id}
                    file={file}
                    onOpen={onOpen}
                    onAnnotate={onAnnotate}
                    onAnalyze={onAnalyze}
                    onPromote={onPromote}
                    onAdjustPoints={onAdjustPoints}
                    onSummaryShare={onSummaryShare}
                    onDownload={onDownload}
                    onDelete={onDelete}
                    shouldShowAdjustPoints={shouldShowAdjustPoints}
                    preferThumbnail={preferThumbnail}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {(hasMore || isLoading) && (
        <InfiniteScroll isLoading={isLoading} hasMore={hasMore} next={onLoadMore} rootMargin="50px" threshold={0}>
          <div className="flex items-center justify-center py-4">
            {isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
};

const EntityMediaViewContent = ({
  onClose,
  headerTitle,
  entityType,
  dashboardColumns = 4,
  dashboardColSpan,
}: EntityWidgetProps) => {
  const {
    files,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFiles,
    isErrorFiles,
    refetch,
    entityRecord,
    handleFileOpen,
    openedFile,
    openedFileMode,
    clearOpenedFileMode,
    isFileEditorOpen,
    setIsFileEditorOpen,
    uploadFiles,
    isUploading: isContextUploading,
    isImportingCompanyCam,
    handleFileDownload,
    deleteFile,
  } = useEntityFileContext();

  const { editorHandler, project } = useSwivuBryntumContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { uploadRef, isUploading: isLocalUploading, openUpload, handleUpload, accept } = useEntityFileUpload({
    category: "media",
    uploadNewFile: uploadFiles,
  });
  const isUploading = isLocalUploading || isContextUploading;

  const { isDraggingOver, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useFileDragAndDrop({
    category: "media",
    uploadFiles,
  });

  /** Extra empty-state upload when the tile is wider than 25% of the dashboard. */
  const colSpan = dashboardColSpan ?? dashboardColumns;
  const showExpandedControls = dashboardColumns > 0 && colSpan / dashboardColumns > 0.25;

  const { shouldShowAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler,
  });

  const imageFiles = useMemo(() => files.filter((f) => f.fileType === "image"), [files]);

  const openWithMode = useCallback(
    (file: SwivuFile, mode: FileViewerMode = "default") => {
      handleFileOpen(file, { mode });
    },
    [handleFileOpen],
  );

  const handleManage = useCallback(() => {
    editorHandler.openEditor(EDITOR_KEYS.taskFilesEditor, entityRecord, {
      entity: entityRecord,
      entityType,
      initialTab: "media",
    });
  }, [editorHandler, entityRecord, entityType]);

  return (
    <>
      <DashboardCard
        onClose={onClose}
        cardContentClassName={cn("h-full flex flex-col p-0", isDraggingOver && "ring-2 ring-primary ring-offset-2")}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        header={
        <div className="flex items-center justify-between w-full gap-2">
          <CardTitle className="text-base gap-2 flex items-center min-w-0 overflow-hidden">
            <span className="truncate">{headerTitle ?? "Media"}</span>
            {totalCount > 0 && (
              <Badge variant="muted" size="xs" className="shrink-0">
                {totalCount}
              </Badge>
            )}
          </CardTitle>

          <div className="flex items-center gap-2 shrink-0">
            {isUploading && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                <Loader2 className="size-3 animate-spin shrink-0" />
                Uploading…
              </span>
            )}
            {!isUploading && isImportingCompanyCam && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                    <Loader2 className="size-3 animate-spin shrink-0" />
                    Importing…
                  </span>
                </TooltipTrigger>
                <TooltipContent>Importing from CompanyCam…</TooltipContent>
              </Tooltip>
            )}
            <ButtonGroup>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="entityHeader" className="w-auto px-3" size="icon" onClick={handleManage}>
                    <Expand className="size-4" /> Manage
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Manage media</TooltipContent>
              </Tooltip>
            </ButtonGroup>
            {showExpandedControls && (
              <input ref={uploadRef} type="file" className="hidden" multiple accept={accept} onChange={handleUpload} />
            )}
          </div>
        </div>
      }>
        {isDraggingOver && <EntityFileDropOverlay label="Drop media files here" />}
        <AnimatePresence mode="wait">
          {isLoadingFiles ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="p-3">
              <div className="grid grid-cols-3 gap-1.5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-md" />
                ))}
              </div>
            </motion.div>
          ) : isErrorFiles ? (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="flex flex-1 items-center justify-center h-full p-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-sm text-muted-foreground">Failed to load media.</p>
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </motion.div>
          ) : imageFiles.length === 0 ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="flex flex-1 items-center justify-center h-full p-4">
              <div className="h-full w-full">
                <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
                  {isImportingCompanyCam ? (
                    <>
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Loader2 className="size-5 animate-spin" />
                      </span>
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-foreground">Importing from CompanyCam</h3>
                        <p className="text-xs text-muted-foreground">Media will appear here shortly.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <ImageIcon className="size-5" />
                      </span>
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-foreground">No media files yet</h3>
                        <p className="text-xs text-muted-foreground">
                          {showExpandedControls ? "Upload media to see them here." : "Open Manage to upload or link CompanyCam."}
                        </p>
                      </div>
                      {showExpandedControls ? (
                        <Button size="sm" onClick={openUpload}>
                          <Upload className="size-4 mr-1" />
                          Upload
                        </Button>
                      ) : (
                        <Button size="sm" onClick={handleManage}>
                          <Expand className="size-4 mr-1" />
                          Manage
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              ref={scrollRef}
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
              className="flex-1 overflow-y-auto overflow-x-hidden"
            >
              <MediaGrid
                images={imageFiles}
                onOpen={(file) => openWithMode(file)}
                onAnnotate={(file) => openWithMode(file, "annotate")}
                onAnalyze={(file) => openWithMode(file, "analyze")}
                onPromote={(file) => openWithMode(file, "promote")}
                onAdjustPoints={(file) => openWithMode(file, "adjustPoints")}
                onSummaryShare={(file) => openWithMode(file, "summaryShare")}
                onDownload={handleFileDownload}
                onDelete={(id) => {
                  void deleteFile(id);
                }}
                shouldShowAdjustPoints={shouldShowAdjustPoints}
                scrollRef={scrollRef}
                hasMore={hasNextPage}
                isLoading={isFetchingNextPage}
                onLoadMore={fetchNextPage}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DashboardCard>

      {openedFile && isFileEditorOpen && (
        <FileDetailModal
          entityRecord={entityRecord}
          file={openedFile}
          navigationFiles={imageFiles}
          open={isFileEditorOpen}
          setOpen={(next) => {
            setIsFileEditorOpen(next);
            if (!next) clearOpenedFileMode();
          }}
          initialViewerMode={openedFileMode}
        />
      )}
    </>
  );
};

export const EntityMediaView = ({
  record,
  entityType,
  onClose,
  headerTitle,
  dashboardColumns,
  dashboardColSpan,
}: EntityWidgetProps) => (
  <EntityFileProvider record={record} entityType={entityType} filters={{ fileType: "image" }}>
    <EntityMediaViewContent
      onClose={onClose}
      headerTitle={headerTitle}
      record={record}
      entityType={entityType}
      dashboardColumns={dashboardColumns}
      dashboardColSpan={dashboardColSpan}
    />
  </EntityFileProvider>
);
